import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ChartPanel = ({ data, loading }) => {
  const [chartType, setChartType] = useState('line');
  const [selectedMetrics, setSelectedMetrics] = useState(['close', 'volume']);
  const [timeframe, setTimeframe] = useState('all');
  const [isFullscreen, setIsFullscreen] = useState(false);

  const chartTypeOptions = [
    { value: 'line', label: 'Line Chart' },
    { value: 'candlestick', label: 'Candlestick Chart' },
    { value: 'bar', label: 'Bar Chart' },
    { value: 'area', label: 'Area Chart' }
  ];

  const metricOptions = [
    { value: 'open', label: 'Open Price' },
    { value: 'high', label: 'High Price' },
    { value: 'low', label: 'Low Price' },
    { value: 'close', label: 'Close Price' },
    { value: 'volume', label: 'Volume' },
    { value: 'openInterest', label: 'Open Interest' }
  ];

  const timeframeOptions = [
    { value: 'all', label: 'All Data' },
    { value: '1d', label: 'Last Day' },
    { value: '7d', label: 'Last Week' },
    { value: '30d', label: 'Last Month' },
    { value: '90d', label: 'Last Quarter' },
    { value: '1y', label: 'Last Year' }
  ];

  const formatTooltipValue = (value, name) => {
    if (name === 'volume' || name === 'openInterest') {
      if (value >= 1000000) {
        return [`${(value / 1000000)?.toFixed(2)}M`, name];
      } else if (value >= 1000) {
        return [`${(value / 1000)?.toFixed(2)}K`, name];
      }
      return [value?.toLocaleString(), name];
    }
    return [Number(value)?.toFixed(2), name];
  };

  const getLineColor = (metric) => {
    const colors = {
      open: '#8B5CF6',
      high: '#10B981',
      low: '#EF4444',
      close: '#3B82F6',
      volume: '#F59E0B',
      openInterest: '#6366F1'
    };
    return colors?.[metric] || '#6B7280';
  };

  const renderChart = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading chart data...</p>
          </div>
        </div>
      );
    }

    if (!data || data?.length === 0) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No data available for the selected filters</p>
          </div>
        </div>
      );
    }

    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="date" 
                stroke="#64748B"
                fontSize={12}
              />
              <YAxis 
                stroke="#64748B"
                fontSize={12}
                tickFormatter={(value) => value?.toFixed(2)}
              />
              <Tooltip 
                formatter={formatTooltipValue}
                labelStyle={{ color: '#0F172A' }}
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #E2E8F0',
                  borderRadius: '6px'
                }}
              />
              <Legend />
              {selectedMetrics?.map((metric) => (
                <Line
                  key={metric}
                  type="monotone"
                  dataKey={metric}
                  stroke={getLineColor(metric)}
                  strokeWidth={2}
                  dot={false}
                  name={metricOptions?.find(opt => opt?.value === metric)?.label}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="date" 
                stroke="#64748B"
                fontSize={12}
              />
              <YAxis 
                stroke="#64748B"
                fontSize={12}
                tickFormatter={(value) => value?.toFixed(2)}
              />
              <Tooltip 
                formatter={formatTooltipValue}
                labelStyle={{ color: '#0F172A' }}
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #E2E8F0',
                  borderRadius: '6px'
                }}
              />
              <Legend />
              {selectedMetrics?.map((metric) => (
                <Bar
                  key={metric}
                  dataKey={metric}
                  fill={getLineColor(metric)}
                  name={metricOptions?.find(opt => opt?.value === metric)?.label}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="date" 
                stroke="#64748B"
                fontSize={12}
              />
              <YAxis 
                stroke="#64748B"
                fontSize={12}
                tickFormatter={(value) => value?.toFixed(2)}
              />
              <Tooltip 
                formatter={formatTooltipValue}
                labelStyle={{ color: '#0F172A' }}
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #E2E8F0',
                  borderRadius: '6px'
                }}
              />
              <Legend />
              {selectedMetrics?.map((metric) => (
                <Line
                  key={metric}
                  type="monotone"
                  dataKey={metric}
                  stroke={getLineColor(metric)}
                  strokeWidth={2}
                  dot={false}
                  name={metricOptions?.find(opt => opt?.value === metric)?.label}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <div className={`bg-card border border-border rounded-lg shadow-sm ${isFullscreen ? 'fixed inset-4 z-50' : ''}`}>
      {/* Chart Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border-b border-border gap-4">
        <div className="flex items-center gap-2">
          <Icon name="TrendingUp" size={20} />
          <h3 className="text-lg font-semibold text-foreground">Historical Price Chart</h3>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <Select
            options={chartTypeOptions}
            value={chartType}
            onChange={setChartType}
            className="w-40"
          />
          
          <Select
            options={metricOptions}
            value={selectedMetrics}
            onChange={setSelectedMetrics}
            multiple
            className="w-48"
          />
          
          <Select
            options={timeframeOptions}
            value={timeframe}
            onChange={setTimeframe}
            className="w-32"
          />
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsFullscreen(!isFullscreen)}
            iconName={isFullscreen ? "Minimize2" : "Maximize2"}
          />
        </div>
      </div>

      {/* Chart Content */}
      <div className="p-4">
        {renderChart()}
      </div>

      {/* Chart Controls */}
      <div className="flex items-center justify-between p-4 border-t border-border">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Icon name="ZoomIn" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Zoom: Mouse wheel</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Move" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Pan: Click & drag</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Export Chart
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Share"
            iconPosition="left"
          >
            Share
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChartPanel;